import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, RefreshCw, Filter, ListChecks, FileText, Star, X, Save } from 'lucide-react';

import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import SourcingRfpService from '../../services/sourcingRFPService';

const BidEvaluation = () => {
  const navigate = useNavigate();
  const { error: showError, success: showSuccess, toasts, removeToast } = useToast();
  
  const [rfps, setRfps] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState('');
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [evalModal, setEvalModal] = useState({ open: false, response: null });
  const [scores, setScores] = useState({ technical: 0, commercial: 0, comments: '' });

  // Load RFPs and Initial Responses (All)
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both simultaneously
      const [rfpRes, responseRes] = await Promise.all([
        SourcingRfpService.getRfps(),
        SourcingRfpService.getResponses('') // Empty string to get ALL
      ]);

      const rfpList = rfpRes.data?.items || rfpRes.items || (Array.isArray(rfpRes.data) ? rfpRes.data : []);
      setRfps(rfpList);
      setResponses(responseRes.data || []);
    } catch (err) {
      showError("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const onRfpSelect = async (id) => {
    setSelectedRfpId(id);
    setLoading(true);
    try {
      // If id is empty, getResponses('') returns everything
      const res = await SourcingRfpService.getResponses(id || '');
      setResponses(res.data || []);
    } catch (err) {
      showError("Failed to filter vendor bids");
    } finally {
      setLoading(false);
    }
  };

  const openEvalModal = (res) => {
      setEvalModal({ open: true, response: res });
      setScores({ technical: 0, commercial: 0, comments: '' });
    };

   const handleScoreSubmit = async () => {
  try {
    const payload = {
      responseId: evalModal.response._id,
      technicalScore: parseFloat(scores.technical),
      commercialScore: parseFloat(scores.commercial),
      // Weighting logic: 60% Technical, 40% Commercial
      weightedScore: (scores.technical * 0.6) + (scores.commercial * 0.4),
      comments: scores.comments
    };

    await SourcingRfpService.submitEvaluation(evalModal.response.rfpId._id, payload);
    showSuccess("Evaluation submitted successfully!");
    setEvalModal({ open: false, response: null });
    loadInitialData(); // Refresh list
  } catch (err) {
    showError(err.message || "Failed to submit score");
  }
};

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'rfpInfo',
      label: 'Sourcing Event',
      // Only show this column if we are looking at "All Data"
      hidden: !!selectedRfpId, 
      render: (_, res) => (
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-gray-400" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-700">{res.rfpId?.title || 'N/A'}</span>
            <span className="text-xs text-gray-400">{res.rfpId?.rfpNumber}</span>
          </div>
        </div>
      )
    },
    {
      key: 'vendor',
      label: 'Vendor',
      render: (_, res) => (
        <div className="flex flex-col">
          <span className="font-bold text-gray-900">{res.vendorId?.name || 'Unknown Vendor'}</span>
          <span className="text-xs text-gray-500">{res.vendorId?.email || 'No email'}</span>
        </div>
      )
    },
    {
      key: 'totalValue',
      label: 'Bid Amount',
      render: (_, res) => {
        const total = res.lineItems?.reduce((sum, item) => sum + (item.price || 0), 0);
        return <span className="text-blue-600 font-bold">${total?.toLocaleString()}</span>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, res) => (
        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
          res.status === 'SUBMITTED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
        }`}>
          {res.status}
        </span>
      )
    },
    {
  key: 'score',
  label: 'Score',
  render: (_, res) => {
    const score = res.evaluationId?.weightedScore;
    return (
      <div className="flex flex-col">
         {score !== undefined ? (
           <span className="font-bold text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-100 w-fit">
             {score.toFixed(2)}%
           </span>
         ) : (
           <span className="text-gray-400 italic text-sm">Not Scored</span>
         )}
      </div>
    );
  }
},
{
  key: 'actions',
  label: 'Actions',
  render: (_, res) => (
    <div className="flex gap-2">
      <button onClick={() => navigate(`/sourcing/responses/view/${res._id}`)} className="p-2 hover:bg-gray-100 rounded-lg"><Eye size={16} /></button>
      <button 
        onClick={() => openEvalModal(res)} 
        className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg flex items-center gap-1"
      >
        <Star size={16} /> Score
      </button>
    </div>
  )
}
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Bid Evaluation" 
        description={selectedRfpId ? "Comparing bids for selected event" : "Overview of all received vendor proposals"}
        actions={[{
          icon: <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />,
          variant: 'outline',
          onClick: loadInitialData
        }]}
      />

      <div className="page-container">
        {/* Selector Section */}
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3 text-gray-400">
            <Filter size={20} />
            <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">Filter by Event:</span>
          </div>
          <select 
            className="flex-1 bg-gray-50 border border-gray-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            value={selectedRfpId}
            onChange={(e) => onRfpSelect(e.target.value)}
          >
            <option value="">Show All Responses</option>
            {rfps.filter(r => r.status === 'PUBLISHED' || r.status === 'Draft').map(rfp => (
              <option key={rfp._id} value={rfp._id}>
                {rfp.rfpNumber} - {rfp.title} ({rfp.status})
              </option>
            ))}
          </select>
        </div>
       {evalModal.open && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
      <div className="p-6 border-b flex justify-between items-center bg-gray-50">
        <h3 className="text-lg font-bold text-gray-800">Evaluate: {evalModal.response.vendorId?.name}</h3>
        <button onClick={() => setEvalModal({open:false})}><X size={20}/></button>
      </div>
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-600 mb-1">Technical Score (0-100)</label>
          <input type="number" className="w-full border rounded-lg p-2" 
            value={scores.technical} onChange={(e) => setScores({...scores, technical: e.target.value})} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-600 mb-1">Commercial Score (0-100)</label>
          <input type="number" className="w-full border rounded-lg p-2" 
            value={scores.commercial} onChange={(e) => setScores({...scores, commercial: e.target.value})} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-600 mb-1">Evaluator Comments</label>
          <textarea className="w-full border rounded-lg p-2 h-24" 
            value={scores.comments} onChange={(e) => setScores({...scores, comments: e.target.value})} />
        </div>
      </div>
      <div className="p-6 bg-gray-50 flex gap-3">
        <button onClick={() => setEvalModal({open:false})} className="flex-1 py-2 border rounded-xl font-semibold">Cancel</button>
        <button onClick={handleScoreSubmit} className="flex-1 py-2 bg-blue-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2">
          <Save size={18}/> Save Evaluation
        </button>
      </div>
    </div>
  </div>
)}
        {/* The Table now always shows data (either filtered or all) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <DataTable 
                data={responses} 
                showActions={false}
                // Filter columns to remove hidden ones (like rfpInfo when an RFP is selected)
                columns={columns.filter(col => !col.hidden)} 
                loading={loading}
                emptyMessage={selectedRfpId ? "No bids for this RFP yet." : "No vendor responses found in the system."}
            />
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default BidEvaluation;